import { gql, useQuery } from "@apollo/client";
import { Button, Card, Col, Divider, Image, Layout, Menu, Row, Typography } from "antd";
import { useApplicationContext } from "components/Application";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import moment from "moment";
import React, { Fragment } from "react";
import { NavLink, useParams } from "react-router-dom";

const { Title, Text } = Typography;

const orderDocumentQuery = gql`
  query($id: ID!) {
    orderDocument(id: $id) {
      id
      code
      status
      updatedAt
      seasonKind
      document
      notes
      user {
        id
        companyName
        address
        city
        zip
        country
        vatNumber
      }
      paymentMethod {
        nameEn
      }
      rows {
        id
        item {
          id
          code
          cover
          color {
            id
            code
            nameEn
            patch
          }
        }
        referenceItem {
          id
          nameEn
          shortDescriptionEn
        }
        sizeQuantities {
          id
          quantity
          size {
            id
            code
          }
        }
        quantity
        price
        total
      }
      quantity
      total
      taxes
      grandTotal
    }
  }
`;

export const Show: React.FC<{}> = () => {
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const { id } = useParams();

  const { error, loading: queryLoading, data } = useQuery(orderDocumentQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="../">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const resource = data.orderDocument;
  const stock = resource.seasonKind === "stock";

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Title level={4} style={{ lineHeight: "auto" }}>
                Cart
              </Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <NavLink to="../">
                <Button>Back</Button>
              </NavLink>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            <Row>
              <Col flex={1}>
                <Row gutter={[25, 25]}>
                  <Col>
                    <Title level={5}>Date</Title>
                    <Text>{moment(resource.updatedAt).format("DD-MM-YYYY")}</Text>
                    <Title level={5}>Customer</Title>
                    <Text>{resource.user.companyName}</Text>
                    <br />
                    <Text>{resource.user.address}</Text>
                    <br />
                    <Text>
                      {resource.user.city} {resource.user.zip} - {resource.user.country}
                    </Text>
                    <br />
                    <Text>Vat Number: {resource.user.vatNumber}</Text>
                  </Col>
                </Row>
                <Row gutter={[50, 25]}>
                  <Col>
                    <Title level={5}>Delivery window</Title>
                    <Text>{stock ? "10 Days from confirmation" : "---"}</Text>
                  </Col>
                  <Col>
                    <Title level={5}>Payment method</Title>
                    <Text>{resource.paymentMethod.nameEn}</Text>
                  </Col>
                </Row>
                {resource.notes && (
                  <Row>
                    <Col>
                      <Title level={5}>Notes</Title>
                      <Text>{resource.notes}</Text>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
            <Divider />
            {resource.rows?.map((row: any, i: any) => (
              <Row gutter={[25, 25]} key={i}>
                <Col span={4}>
                  <Image src={`${appEndpointStore}${row.item.cover}`} width={100} />
                </Col>
                <Col span={6}>
                  <b>{row.referenceItem.nameEn}</b>
                  <br />
                  {row.referenceItem.shortDescriptionEn}
                  <br />
                  <Image src={`${appEndpointStore}${row.item.color.patch}`} width={20} height={20} /> {row.item.color.code} -{" "}
                  {row.item.color.nameEn}
                </Col>
                <Col span={11}>
                  <Row gutter={[25, 25]}>
                    {row.sizeQuantities?.map((sizeQuantity: any, i: any) => (
                      <Col key={i}>
                        {sizeQuantity.quantity} / {sizeQuantity.size.code}
                      </Col>
                    ))}
                  </Row>
                </Col>
                <Col span={3}>
                  Qty: {row.quantity}
                  <br />
                  Price: {row.price}
                  <br />
                  Total: {row.total}
                </Col>
              </Row>
            ))}
            <Divider />
            <Row>
              <Col flex={1}>
                <Title level={5}>Summary</Title>
              </Col>
              <Col flex={2}>
                <Row justify="end">
                  <Col span={4}>
                    <Title level={5}>Quantity</Title>
                    {resource.quantity}
                  </Col>
                  <Col span={4}>
                    <Title level={5}>Total</Title>
                    {resource.total}
                  </Col>
                  <Col span={4}>
                    <Title level={5}>Taxes</Title>
                    {resource.taxes}
                  </Col>
                  <Col span={4}>
                    <Title level={5}>Grand Total</Title>
                    {resource.grandTotal}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
